import React, { Fragment, useEffect } from "react";
import { featuresKey, supabase } from "../constants";
import { useDispatch } from "react-redux";
import { removeStorageItem, setStorageItem } from "../utils/common-utils";
import {
  getUserDetails,
  getUserSession,
  getUserUUID,
} from "../redux/reducers/public/public-action";
import { getFeatureData } from "../api/supabaseClient";
import { OWNER_ONLY_ALLOWED_EMAIL, getSessionEmail } from "../utils/access-control";
import { captureLegacyPortfolioOwner } from "../utils/portfolio-storage";

const DEFAULT_ZERODHA_WORKER_URL = "http://localhost:8788";
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

const getZerodhaWorkerBaseUrl = () => {
  const explicitUiUrl = String(process.env.REACT_APP_ZERODHA_WEB_UI_URL || "").trim();
  if (explicitUiUrl) {
    try {
      return new URL(explicitUiUrl).origin;
    } catch (_error) {
      return explicitUiUrl.replace(/\/+$/, "");
    }
  }

  if (typeof window !== "undefined" && LOCAL_HOSTNAMES.has(window.location.hostname)) {
    return DEFAULT_ZERODHA_WORKER_URL;
  }

  const streamUrl = String(
    process.env.REACT_APP_ZERODHA_STREAM_URL || "ws://localhost:8788"
  ).trim();
  try {
    const httpUrl = streamUrl.startsWith("wss://")
      ? streamUrl.replace(/^wss:\/\//, "https://")
      : streamUrl.startsWith("ws://")
        ? streamUrl.replace(/^ws:\/\//, "http://")
        : streamUrl;
    return new URL(httpUrl).origin;
  } catch (_error) {
    return DEFAULT_ZERODHA_WORKER_URL;
  }
};

const enableAuthenticatedDailyLossGuard = async () => {
  const streamToken = String(process.env.REACT_APP_ZERODHA_STREAM_TOKEN || "").trim();
  const headers = { "Content-Type": "application/json" };
  if (streamToken) headers["X-Stream-Token"] = streamToken;
  const response = await fetch(`${getZerodhaWorkerBaseUrl()}/api/daily-loss-guard/enable`, {
    method: "POST",
    headers,
    body: JSON.stringify({ confirm_live_orders: true }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || `Daily loss guardrail activation failed (${response.status}).`);
  }
};

const AuthAPIs = ({ activeUser, setLoading }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeUser) {
      const getLoggedInUser = (setLoading) => {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
          } else {
            removeStorageItem([
              "userId",
              "userSession",
              "subscription",
              "ledgerData",
              "token",
              "features",
            ]);
          }
          setLoading(false);
        });
      };

      getLoggedInUser(setLoading);
    }
  }, [activeUser, setLoading]);

  useEffect(() => {
    if (getSessionEmail(activeUser) !== OWNER_ONLY_ALLOWED_EMAIL) return undefined;

    let cancelled = false;
    let retryTimer = null;
    let retryCount = 0;
    const enableGuardrail = async () => {
      try {
        await enableAuthenticatedDailyLossGuard();
      } catch (_error) {
        if (cancelled) return;
        const retryDelay = Math.min(30000, 2000 * (2 ** Math.min(retryCount, 4)));
        retryCount += 1;
        retryTimer = window.setTimeout(enableGuardrail, retryDelay);
      }
    };

    enableGuardrail();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [activeUser]);

  useEffect(() => {
    captureLegacyPortfolioOwner();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      localStorage.setItem("userSession", JSON.stringify(session));

      dispatch(getUserSession(session));

      setLoading(false);
      if (event === "SIGNED_IN") {
        localStorage.setItem("userId", session.user.id);
        localStorage.setItem("token", session.access_token);
        getFeatureData(session.user.id).then(data => setStorageItem(featuresKey, data));
        dispatch(getUserUUID(session.user.id));
        dispatch(getUserDetails(session.user));
      }
    });

    localStorage.setItem("subscription", JSON.stringify(subscription));

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addUserPosition = async (userId) => {
    const randomPositionId = Math.floor(Math.random() * 1000);

    const { error } = await supabase.from("users_positions").insert({
      user_id: userId,
      position_id: randomPositionId,
    });
    if (error) {
    }
  };

  return <Fragment></Fragment>;
};

export default AuthAPIs;
